import React, { useState, useEffect } from "react";
import {
    Autocomplete,
    TextField,
    CircularProgress,
    Box,
    Button,
    Typography,
} from "@mui/material";
import { Person, PersonAdd } from "@mui/icons-material";
import { Customer } from "../../types";
import { customerService } from "../../services/customerService";
import { getCustomerFullName } from "../../utils/customerUtils";

interface CustomerSearchAutocompleteProps<
    Multiple extends boolean | undefined
> {
    value: Multiple extends true ? Customer[] : Customer | null;
    onChange: (
        value: Multiple extends true ? Customer[] : Customer | null
    ) => void;
    multiple?: Multiple;
    error?: boolean;
    helperText?: string;
    label?: string;
    disabled?: boolean;
    required?: boolean;
    /** The text input, e.g. to focus it when a dialog opens. */
    inputRef?: React.Ref<HTMLInputElement>;
    /** Highlight the top match as results arrive, so Enter picks it. */
    autoHighlight?: boolean;
    /**
     * Enter while the suggestions list is closed (e.g. after a customer was
     * just picked, or nothing typed). Enter inside an open list still picks
     * the highlighted customer as usual.
     */
    onEnterKey?: () => void;
    /**
     * When nothing matches, offer "New customer '<typed text>'" — called
     * with the text typed so far.
     */
    onCreateNew?: (typed: string) => void;
    /** The text in the box as it's typed (e.g. to carry it into a form). */
    onSearchTextChange?: (text: string) => void;
}

export const CustomerSearchAutocomplete = <
    Multiple extends boolean | undefined = false
>({
    value,
    onChange,
    multiple = false as Multiple,
    error = false,
    helperText = "",
    label = "Search Customer",
    disabled = false,
    required = false,
    inputRef,
    autoHighlight = false,
    onEnterKey,
    onCreateNew,
    onSearchTextChange,
}: CustomerSearchAutocompleteProps<Multiple>) => {
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState<Customer[]>([]);
    const [loading, setLoading] = useState(false);
    const [inputValue, setInputValue] = useState("");

    // Sync inputValue with value when value changes externally (e.g. from props)
    useEffect(() => {
        if (!multiple) {
            if (value && !Array.isArray(value)) {
                setInputValue(getCustomerFullName(value));
            } else if (value === null) {
                setInputValue("");
            }
        }
    }, [value, multiple]);

    // Search customers function
    const searchCustomers = async (searchTerm: string) => {
        if (!searchTerm || searchTerm.length < 2) {
            setOptions([]);
            return;
        }

        setLoading(true);
        try {
            const response = await customerService.getCustomers({
                search: searchTerm,
                limit: 20,
                active_only: true,
            });
            setOptions(response.customers || []);
        } catch (error) {
            console.error("Error searching customers:", error);
            setOptions([]);
        } finally {
            setLoading(false);
        }
    };

    // Debounced search when input changes
    useEffect(() => {
        if (open && inputValue) {
            const timer = setTimeout(() => {
                searchCustomers(inputValue);
            }, 500);
            return () => clearTimeout(timer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputValue, open]);

    // Load initial options when opened
    useEffect(() => {
        if (open && options.length === 0 && !inputValue) {
            setLoading(true);
            customerService
                .getCustomers({ limit: 20, active_only: true })
                .then((response) => {
                    setOptions(response.customers || []);
                })
                .catch((error) => {
                    console.error("Error loading customers:", error);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    return (
        <Autocomplete
            multiple={multiple}
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            value={value}
            onChange={(_, newValue) => onChange(newValue as any)}
            inputValue={inputValue}
            onInputChange={(_, newInputValue) => {
                setInputValue(newInputValue);
                onSearchTextChange?.(newInputValue);
            }}
            options={options}
            filterOptions={(options) => (Array.isArray(options) ? options : [])} // We handle filtering server-side
            getOptionKey={(option) => option.customer_id}
            getOptionLabel={(option) => getCustomerFullName(option)}
            loading={loading}
            disabled={disabled}
            autoHighlight={autoHighlight}
            onKeyDown={(event) => {
                if (
                    onEnterKey &&
                    event.key === "Enter" &&
                    !open &&
                    !event.nativeEvent.isComposing
                ) {
                    event.preventDefault();
                    onEnterKey();
                }
            }}
            isOptionEqualToValue={(option, value) =>
                option.customer_id === value.customer_id
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    inputRef={inputRef}
                    label={label}
                    error={error}
                    helperText={helperText}
                    required={required}
                    InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                            <>
                                {multiple ? null : (
                                    <Person
                                        sx={{ mr: 1, color: "action.active" }}
                                    />
                                )}
                                {params.InputProps.startAdornment}
                            </>
                        ),
                        endAdornment: (
                            <>
                                {loading ? (
                                    <CircularProgress
                                        color="inherit"
                                        size={20}
                                    />
                                ) : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
            renderOption={(props, option) => (
                <Box component="li" {...props} key={option.customer_id}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            width: "100%",
                        }}
                    >
                        <Person color="action" />
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="body2">
                                {getCustomerFullName(option)}
                            </Typography>
                            {option.phone && (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    {option.phone}
                                </Typography>
                            )}
                            {option.email && (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ ml: 1 }}
                                >
                                    {option.email}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Box>
            )}
            noOptionsText={
                inputValue.length < 2 ? (
                    "Type at least 2 characters to search"
                ) : onCreateNew ? (
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
                        <span>No customers found</span>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<PersonAdd />}
                            // Keep the input focused so the list doesn't
                            // close before the click lands.
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                                setOpen(false);
                                onCreateNew(inputValue.trim());
                            }}
                        >
                            New customer “{inputValue.trim()}”
                        </Button>
                    </Box>
                ) : (
                    "No customers found"
                )
            }
        />
    );
};
